// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import type { AppState } from '@shared/data-access/config/citec-app';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Translation } from '@shared/ui-kit';
import {
  type SingleTickerTag,
  type TickersTags,
  TickerTagFeature,
} from '@shared/data-access/interfaces/api/engine/citec';
import { isUndefined } from '@shared/utils';
import {
  MODAL_TEMPLATES_OPTIONS,
  showModal,
  type ShowModalOptions,
} from '@shared/ui-kit/components/popups/show-modal';
import { useSafeUpdate } from '@shared/ui-kit/hooks';
import { AppIcon } from '@shared/ui-kit/components/app-icon';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAllTags,
  selectSelectTag,
  selectTag,
  selectTagsByTicker,
  selectTagsNames,
  selectTagSuggestions,
  tagManagerSlice,
} from '@shared/data-access/slices/tag-manager';
import { type MyInvestments } from '@shared/data-access/interfaces/common/my-investment';
import { type WatchListPosition } from '@shared/data-access/interfaces/redux/my-research-state';
import { addWatchListPosition } from '@shared/data-access/slices/tag-manager/tag-manager-thunk';
import { selectFeature } from '@shared/data-access/utils/toggle-feature';
import {
  type AssetLogoType,
  getAssetLogo,
} from '@shared/ui-kit/components/asset-logo';
import { tickerTagApi } from '@shared/data-access/api/engine/ticker-tag-api';
import { buildAggregatorAccountTagManagerPositions } from '@shared/data-access/transformers/advisory/build-aggregator-account-positions';
import {
  concatenateStrings,
  groupTickersTag,
  tickerTagsActualization,
} from '../utils/ticker-tags-actualization';
import type { Tag } from '@shared/data-access/interfaces/redux/tag-manager-state';
import { type TagsSuggestions } from '../types';
import { type AccountsHoldingsData } from '@shared/data-access/interfaces/api/brokerage/brokerage';

export type ManageTagsOptions =
  | {
      feature: TickerTagFeature.PortfolioPositions;
      dataSource: MyInvestments[];
    }
  | {
      feature: TickerTagFeature.SelectedAssets;
      dataSource: WatchListPosition[];
    }
  | {
      feature: TickerTagFeature.AggregatorPositions;
      dataSource: AccountsHoldingsData[];
    };

export const useHandlerManageTags = ({
  feature,
  dataSource,
}: ManageTagsOptions) => {
  const { t } = useTranslation();
  const runSafeUpdate = useSafeUpdate();

  const dispatch = useDispatch<any>();

  const [createTickerTag] = tickerTagApi.useCreateTickerTagMutation();
  const [deleteTickerTag] = tickerTagApi.useDeleteTickerTagMutation();

  const currentState = useSelector((state: AppState) => state);
  const tagNames = useSelector((state: AppState) =>
    selectTagsNames(state, feature),
  );
  const tags = useSelector((state: AppState) => selectAllTags(state, feature));
  const tagName = useSelector(
    (state: AppState) => selectSelectTag(state, feature)!,
  );
  const tag = useSelector((state: AppState) => selectTag(state, feature, tagName));

  /* istanbul ignore next */
  const tickerTags = useSelector(
    useCallback(
      (state: AppState) => {
        const transformedData = selectFeature(
          {
            [TickerTagFeature.PortfolioPositions]: () =>
              (dataSource as MyInvestments[]).flatMap(({ subRows }) =>
                subRows.map(({ ticker, asset_class, name = '', url_logo }) => ({
                  name,
                  assetClass: asset_class,
                  ticker,
                  isin: ticker,
                  url_logo,
                })),
              ),
            [TickerTagFeature.SelectedAssets]: () =>
              (dataSource as WatchListPosition[]).map(
                ({ ticker, asset_class, name, isin, url_logo }) => ({
                  assetClass: asset_class,
                  name,
                  ticker,
                  isin,
                  url_logo,
                }),
              ),
            [TickerTagFeature.AggregatorPositions]: () => {
              const uniqueItems = (dataSource as AccountsHoldingsData[]).filter(
                (item, index, arr) =>
                  arr.findIndex(
                    i => (i.ticker ?? i.isin) === (item.ticker ?? item.isin),
                  ) === index,
              );

              return uniqueItems
                .filter(item => item.ticker !== null || item.isin !== null)
                .map(item => ({
                  ticker: (item.ticker ?? item.isin)!,
                  isin: item.isin,
                  name: item.name,
                  assetClass: item.asset_class!,
                  url_logo: getAssetLogo(
                    item.details?.url_logo,
                    item.asset_type as AssetLogoType,
                  ),
                }));
            },
          },
          feature,
        )();

        return selectTagsByTicker(state, feature, transformedData);
      },
      [dataSource, feature],
    ),
  );

  /* istanbul ignore next */
  const positions = useMemo(() => {
    if (isUndefined(tag)) {
      switch (feature) {
        case TickerTagFeature.SelectedAssets:
          return tags
            .map(({ name, tickers }) => ({
              name: name,
              subRows: dataSource
                .filter(({ ticker }) => tickers.map(t => t).includes(ticker))
                .map(asset => ({ ...asset, tag: name })),
            }))
            .concat({
              name: t('tag-manager.non-tagged', '', { ns: 'components' }),
              subRows: dataSource
                .filter(
                  ({ ticker }) =>
                    !tags.flatMap(tag => tag.tickers.map(t => t)).includes(ticker),
                )
                .map(asset => ({ ...asset, tag: '' })),
            })
            .filter(tag => tag.subRows.length);
        default:
          return dataSource;
      }
    }

    switch (feature) {
      case TickerTagFeature.PortfolioPositions: {
        return dataSource
          .filter(({ subRows }) =>
            subRows.some(({ ticker }) => tag.tickers.includes(ticker)),
          )
          .map(investment => {
            const subRows = investment.subRows.filter(({ ticker }) =>
              tag.tickers.includes(ticker),
            );

            return {
              ...investment,
              subRows: subRows,
              quantity: subRows.reduce(
                (current, next) => current + next.quantity,
                0,
              ),
              investment: subRows.reduce(
                (current, next) => current + next.investment,
                0,
              ),
              total_gain_loss: [
                subRows.reduce(
                  (current, next) =>
                    current + next.total_gain_loss.reduce((x, y) => x + y),
                  0,
                ),
              ],
            };
          });
      }

      case TickerTagFeature.SelectedAssets:
        return [
          {
            name: tag.name,
            subRows: dataSource
              .filter(({ ticker }) => tag.tickers.includes(ticker))
              .map(asset => ({ ...asset, tagName: tag.name })),
          },
        ].filter(tag => tag.subRows.length);

      case TickerTagFeature.AggregatorPositions:
        return buildAggregatorAccountTagManagerPositions(
          dataSource.filter(
            ({ ticker, isin }) =>
              (ticker !== null || isin !== null) &&
              tag.tickers.includes((ticker ?? isin)!),
          ),
        );
    }
  }, [dataSource, feature, t, tag, tags]);

  const suggestions = useSelector(
    useCallback(
      (state: AppState) => selectTagSuggestions(state, feature),
      [feature],
    ),
  );

  const showConfirmationModal = useCallback(
    (onConfirm?: () => Promise<void>, onDeny?: () => void, content?: string) => {
      /* istanbul ignore next */
      showModal({
        ...MODAL_TEMPLATES_OPTIONS(t).EDIT_SAVED_PORTFOLIO,
        icon: <AppIcon icon="ExclamationMark" fill="#1072BA" />,
        title: t('tag-manager.save-changes', '', { ns: 'components' }),
        content:
          content ||
          t('tag-manager.save-changes-confirmation', '', { ns: 'components' }),
        buttons: {
          deny: {
            show: true,
            text: content
              ? t('common.close', '', { ns: 'common' })
              : t('common.cancel', '', { ns: 'common' }),
          },
          confirm: { show: true, text: t('common.save', '', { ns: 'common' }) },
        },
        preConfirm: onConfirm,
      } as ShowModalOptions).then(
        ({ isDenied }) => isDenied && runSafeUpdate(() => onDeny?.()),
      );
    },
    [runSafeUpdate, t],
  );

  const onChangeTag = useCallback(
    (tag: string) => dispatch(tagManagerSlice.actions.setSelectTag([feature, tag])),
    [dispatch, feature],
  );

  /* istanbul ignore next */
  const onCreateTag = useCallback(
    async (
      tag: string,
      includeTickers = false,
      addDraftTag: (tag: string, tickers: string[]) => Promise<void>,
      selectedSuggestion?: TagsSuggestions,
    ) => {
      if (includeTickers) {
        const { isConfirmed, isDenied, isDismissed } = await showModal({
          ...MODAL_TEMPLATES_OPTIONS(t).CONFIRM_DELETE,
          title: t('tag-manager.tag-creation-confirmation', '', {
            ns: 'components',
          }),
          content: (
            <Translation
              i18nKey="tag-manager.tag-creation-message"
              ns="components"
              components={{
                p: <p />,
                br: <br />,
              }}
            />
          ),
          buttons: {
            deny: {
              show: true,
              text: t('tag-manager.no-just-add-tag', '', { ns: 'components' }),
            },
            confirm: {
              show: true,
              text: t('tag-manager.yes-assign-positions', '', { ns: 'components' }),
            },
          },
        } as ShowModalOptions);

        if (isDismissed) return;

        try {
          if (isConfirmed) {
            const { tagName: suggestedTag, feature: suggestedFeature } =
              selectedSuggestion as TagsSuggestions;

            const suggestedTagTickers = selectTag(
              currentState,
              suggestedFeature,
              suggestedTag,
            )?.tickers;

            addDraftTag(suggestedTag, suggestedTagTickers || []);
          } else if (isDenied) {
            addDraftTag(tag, []);
          }
        } catch (error) {
          console.error('[useHandlerManageTags]: ', error);
        }
      } else {
        try {
          addDraftTag(tag, []);
        } catch (error) {
          console.error('[useHandlerManageTags]: ', error);
        }
      }
    },
    [t, currentState],
  );

  const onCreateTickerTag = useCallback(
    async (
      initialGroups: Tag[],
      updatedGroups: Tag[],
      initialTickers: TickersTags[],
      updatedTickers: TickersTags[],
      deletedTags: string[],
      deletedTickerTags: SingleTickerTag[],
      onClose: () => void,
      content?: string,
    ) => {
      const differences = tickerTagsActualization(
        feature,
        initialGroups,
        updatedGroups,
        initialTickers,
        updatedTickers,
      );

      const concatenatedStrings = concatenateStrings(deletedTags);
      const deletedTickerTagsGrouped = groupTickersTag(deletedTickerTags);
      /* istanbul ignore next */
      showConfirmationModal(
        async () => {
          const allPromises = [];

          if (differences.length > 0) {
            allPromises.push(createTickerTag({ payload: differences }).unwrap());
          }

          if (concatenatedStrings) {
            allPromises.push(
              deleteTickerTag({ tags: concatenatedStrings, feature }).unwrap(),
            );
          }

          if (deletedTickerTagsGrouped.length > 0) {
            const tickerTagPromises = deletedTickerTagsGrouped.map(
              ({ tag, ticker }) =>
                deleteTickerTag({ tags: tag, tickers: ticker, feature }).unwrap(),
            );
            allPromises.push(...tickerTagPromises);
          }

          await Promise.allSettled(allPromises);

          onClose();
        },
        content ? () => onClose() : undefined,
        content,
      );
    },
    [createTickerTag, feature, showConfirmationModal, deleteTickerTag],
  );

  const onAddTicker = useCallback(
    (ticker: string, assetType: string) => {
      /* istanbul ignore if */
      if (typeof dataSource.find(({ ticker: t }) => t === ticker) !== 'undefined') {
        showModal({
          ...MODAL_TEMPLATES_OPTIONS(t).WARN,
          content: (
            <Translation
              i18nKey="tag-manager.asset-already-in-watchlist-content"
              ns="components"
              components={{
                p: <p />,
                br: <br />,
              }}
              values={{ ticker }}
            />
          ),
          title: t('tag-manager.asset-already-exists', '', { ns: 'components' }),
          buttons: {
            confirm: {
              show: true,
              text: t('tag-manager.got-it', '', { ns: 'components' }),
            },
          },
        } as ShowModalOptions);
        return;
      }

      dispatch(addWatchListPosition({ ticker, assetType }));
    },
    [dataSource, dispatch, t],
  );

  return {
    selectTag: tagName,
    tag,
    tags,
    tagNames,
    onCreateTag,
    onCreateTickerTag,
    onChangeTag,
    onAddTicker,
    positions,
    tickerTags,
    suggestions,
  };
};
